import type { Style, BaseDataItem } from '@wix/thunderbolt-becky-types'
import type { DataSource, AtomKey, Atoms } from '@wix/thunderbolt-signals-vsm-types'
import type { Ref } from '@wix/thunderbolt-types'

export const DATA_NAMESPACE = 'document_data'
export const THEME_DATA_NAMESPACE = 'theme_data'

/** Environment atoms are stored at 'environment.atoms.<key>' */
export const getAtom = <K extends AtomKey>(source: DataSource, key: K): Atoms[K] => {
	const ref: Ref<Atoms[K]> = { $type: 'ref', refPath: ['environment', 'atoms', key] }
	return source.getEnvironment(ref)
}

/** Strip leading '#' from ref IDs */
export function removeHash(id: string | undefined): string {
	if (id === undefined || id === '') return ''
	const s = String(id)
	return s.startsWith('#') ? s.slice(1) : s
}

/** Render flags are stored at 'environment.renderFlags.<flag>' */
export const getRenderFlagValue = <T>(source: DataSource, flag: string): T | undefined => {
	const ref: Ref<T> = { $type: 'ref', refPath: ['environment', 'renderFlags', flag] }
	return source.getEnvironment(ref)
}

type RefArrayLike = BaseDataItem<'RefArray'> & { values?: Array<any> }
type VariantRelationLike = BaseDataItem<'VariantRelation'> & { to?: unknown }

/**
 * Port of Carmi's findDefaultStyle (createComponentsApi.carmi.ts:228-243).
 * If the entry is a RefArray, returns the first non-VariantRelation in `values`,
 * falling back to the first entry's `to` if all values are VariantRelations.
 * Otherwise returns the entry as-is.
 */
export function findDefaultRefArrayEntry<T>(item: unknown): T | undefined {
	if (!item) {
		return undefined
	}
	const baseItem = item as BaseDataItem
	if (baseItem.type !== 'RefArray') {
		return item as T
	}
	const values = (item as RefArrayLike).values
	if (!values || values.length === 0) {
		return undefined
	}
	const defaultEntry = values.find((entry: BaseDataItem) => entry.type !== 'VariantRelation')
	if (defaultEntry) {
		return defaultEntry as T
	}
	const firstEntry = values[0] as VariantRelationLike
	return (firstEntry.to as T) ?? undefined
}

export const findDefaultStyle = (compStyleItem: unknown): Style | undefined =>
	findDefaultRefArrayEntry<Style>(compStyleItem)

/**
 * Merges style properties with spx and themeOverrides.
 * Port of getStylePropertiesWithSpxAndOverrides from thunderbolt-becky-root/src/functionLibrary.ts:443-446.
 */
/** Iterate all keys in a namespace and return items matching the given type */
export function getItemsByTypeFromNamespace<T extends BaseDataItem>(
	source: DataSource,
	namespace: string,
	type: string
): { [id: string]: T } {
	const keys = source.getDataKeys(namespace)
	const result: { [id: string]: T } = {}
	for (const key of keys) {
		const item = source.getResolvedData<T>(namespace, key)
		if (item && item.type === type) {
			result[key] = item
		}
	}
	return result
}

export function getStylePropertiesWithSpxAndOverrides(style: Style): Record<string, any> {
	const { spx, themeOverrides, style: innerStyle, cssProperties, cssCustomProperties } = style
	return { ...innerStyle?.properties, ...cssProperties, ...cssCustomProperties, spx, themeOverrides }
}
